import { useState, useEffect, useRef } from 'react';
import { Heart, MessageCircle, Share2, MoreVertical, ChevronLeft, ChevronRight, Edit2, Pin, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import ChecklistView from './ChecklistView';
import GlowBadge from './GlowBadge';
import VideoPlayer from './VideoPlayer';

export default function PostCard({ post, currentUser, onLike, onPostUpdate }) {
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCaptions, setEditCaptions] = useState([]);
  const [editChecklistId, setEditChecklistId] = useState('');
  const [userChecklists, setUserChecklists] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showBottomShareMenu, setShowBottomShareMenu] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const menuRef = useRef(null);
  const shareMenuRef = useRef(null);

  const isOwnPost = currentUser?.user_id === post.user_id;

  useEffect(() => {
    if (post.checklist_id) {
      fetchChecklist();
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && post.checklist_id) {
        fetchChecklist();
      }
    };
    const handleChecklistUpdated = (e) => {
      if (post.checklist_id && e?.detail?.checklistId === post.checklist_id) {
        fetchChecklist();
      }
    };
    const handleWindowFocus = () => {
      if (post.checklist_id) fetchChecklist();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('checklist:updated', handleChecklistUpdated);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('checklist:updated', handleChecklistUpdated);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [post.checklist_id]);

  const fetchChecklist = async () => {
    try {
      const response = await authFetch(`${API}/checklists/${post.checklist_id}`);
      if (response.ok) {
        const checklistData = await response.json();
        setChecklist(checklistData);
        return;
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
    }
    // Fallback: fetch user's pinned or first checklist
    try {
      const res = await authFetch(`${API}/checklists?user_id=${post.user_id}`);
      if (res.ok) {
        const checklists = await res.json();
        if (checklists.length > 0) {
          const pinned = checklists.find(c => c.is_pinned) || checklists[0];
          setChecklist(pinned);
        }
      }
    } catch (e) { console.error('Fallback checklist fetch error:', e); }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;

    try {
      const response = await authFetch(`${API}/posts/${post.post_id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText })
      });

      if (response.ok) {
        setCommentText('');
        window.location.reload();
      }
    } catch (error) {
      console.error('Error commenting:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowBottomShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditPost = async () => {
    setEditCaptions(post.captions || mediaUrls.map(() => ''));
    setEditChecklistId(post.checklist_id || '');
    setShowEditModal(true);
    setShowMenu(false);
    // Fetch user's checklists for the dropdown
    try {
      const res = await authFetch(`${API}/checklists`);
      if (res.ok) setUserChecklists(await res.json());
    } catch (e) { console.error('Error fetching checklists:', e); }
  };

  const handleSaveEdit = async () => {
    try {
      const body = { captions: editCaptions };
      if (editChecklistId && editChecklistId !== post.checklist_id) {
        body.checklist_id = editChecklistId;
      }
      const response = await authFetch(`${API}/posts/${post.post_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success('Post updated!');
        setShowEditModal(false);
        if (onPostUpdate) onPostUpdate();
        else window.location.reload();
      } else {
        toast.error('Failed to update post');
      }
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
    }
  };

  const handlePinPost = async () => {
    try {
      const response = await authFetch(`${API}/posts/${post.post_id}/pin`, {
        method: 'POST',
      });

      if (response.ok) {
        toast.success(post.is_pinned ? 'Post unpinned' : 'Post pinned to profile!');
        setShowMenu(false);
        if (onPostUpdate) onPostUpdate();
        else window.location.reload();
      } else {
        toast.error('Failed to pin post');
      }
    } catch (error) {
      console.error('Error pinning post:', error);
      toast.error('Failed to pin post');
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const response = await authFetch(`${API}/posts/${post.post_id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Post deleted');
        setShowMenu(false);
        if (onPostUpdate) onPostUpdate();
        else window.location.reload();
      } else {
        toast.error('Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleShare = (platform) => {
    const postUrl = `${window.location.origin}/post/${post.post_id}`;
    const shareText = `Check out this post by @${displayName} on Gloverse! 💄✨`;
    
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + postUrl)}`,
      instagram: `https://www.instagram.com/`, // Instagram doesn't support direct sharing via URL
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}&quote=${encodeURIComponent(shareText)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(shareText)}`
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(postUrl);
      toast.success('Link copied to clipboard!');
    } else if (platform === 'instagram') {
      // For Instagram, copy the link since direct sharing isn't supported
      navigator.clipboard.writeText(postUrl);
      toast.success('Link copied! Open Instagram and paste in your story or DM');
      window.open('https://www.instagram.com/', '_blank');
    } else if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
    setShowBottomShareMenu(false);
    setShowMenu(false);
  };

  // Handle both old posts with image_url and new posts with media_urls
  const mediaUrls = post.media_urls || (post.image_url ? [post.image_url] : []);
  const videoUrl = post.video_url || '';
  const captions = post.captions || [];
  
  // Build slides: video first (if exists), then images, then checklist
  const allSlides = [];
  if (videoUrl) allSlides.push({ type: 'video', url: videoUrl });
  mediaUrls.forEach((url, i) => allSlides.push({ type: 'image', url, caption: captions[i] || '' }));
  if (checklist) allSlides.push({ type: 'checklist' });
  
  const totalSlides = allSlides.length;
  const currentSlide = allSlides[currentSlideIndex] || allSlides[0];

  const nextSlide = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        nextSlide(); // Swipe left = next
      } else {
        prevSlide(); // Swipe right = prev
      }
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const isLiked = post.likes?.includes(currentUser?.user_id);
  const timeAgo = new Date(post.created_at).toLocaleDateString();
  const displayName = post.username || post.user_name?.replace(/\s+/g, '').toLowerCase();

  return (
    <div className="post-card fade-in" data-testid={`post-${post.post_id}`}>
      {/* User Header */}
      <div className="p-4 flex items-center justify-between">
        <button 
          onClick={() => navigate(`/profile/${post.user_id}`)}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          {post.user_picture ? (
            <img src={post.user_picture} alt={displayName} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
              {displayName?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              @{displayName}
            </span>
            <GlowBadge tier={post.glow_badge} />
            {post.is_pinned && (
              <Pin className="w-4 h-4 text-primary fill-primary" />
            )}
          </div>
        </button>
        
        {/* 3-dots Menu */}
        <div className="relative" ref={menuRef}>
          <button 
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-primary/10 rounded-full transition-colors"
            data-testid={`post-menu-${post.post_id}`}
          >
            <MoreVertical className="w-5 h-5 text-muted-foreground" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-border p-2 z-50 min-w-[180px]">
              {/* Share Option */}
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <Share2 className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">Share</span>
              </button>
              
              {showShareMenu && (
                <div className="pl-4 space-y-1 mb-2">
                  <button onClick={() => handleShare('whatsapp')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-green-50 rounded-lg text-sm">
                    <span className="text-green-600">📱</span> WhatsApp
                  </button>
                  <button onClick={() => handleShare('instagram')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-pink-50 rounded-lg text-sm">
                    <span className="text-pink-600">📸</span> Instagram
                  </button>
                  <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 rounded-lg text-sm">
                    <span className="text-blue-500">🐦</span> Twitter
                  </button>
                  <button onClick={() => handleShare('copy')} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg text-sm">
                    <span>🔗</span> Copy Link
                  </button>
                </div>
              )}
              
              {/* Edit Option - Only for own posts */}
              {isOwnPost && (
                <>
                  <button
                    onClick={handleEditPost}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 rounded-lg transition-colors text-left"
                  >
                    <Edit2 className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Edit Post</span>
                  </button>
                  
                  {/* Pin Option */}
                  <button
                    onClick={handlePinPost}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 rounded-lg transition-colors text-left"
                  >
                    <Pin className={`w-5 h-5 ${post.is_pinned ? 'text-primary fill-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium">{post.is_pinned ? 'Unpin Post' : 'Pin to Profile'}</span>
                  </button>
                  
                  {/* Delete Option */}
                  <button
                    onClick={handleDeletePost}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 rounded-lg transition-colors text-left text-red-600"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span className="font-medium">Delete Post</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Swipable Carousel */}
      {(mediaUrls.length > 0 || checklist) && (
        <div 
          className="relative select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Content Area */}
          <div className="relative overflow-hidden">
            {currentSlide?.type === 'checklist' ? (
              /* Checklist Slide */
              <div className="w-full min-h-[300px] bg-gradient-to-br from-primary/5 to-accent/5 p-6 pt-12 flex flex-col">
                <div className="flex-1">
                  <ChecklistView checklist={checklist} compact showShare />
                </div>
              </div>
            ) : currentSlide?.type === 'video' ? (
              /* Video Slide */
              <VideoPlayer src={currentSlide.url} autoPlay />
            ) : (
              /* Image Slide */
              <div className="relative">
                <img 
                  src={currentSlide?.url} 
                  alt={`Slide ${currentSlideIndex + 1}`} 
                  className="w-full max-h-[70vh] object-contain bg-black"
                  draggable={false}
                />
                {/* Per-image caption overlay */}
                {currentSlide?.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-8">
                    <p className="text-white text-sm">{currentSlide.caption}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Slide Counter */}
          <div className="absolute top-3 right-3 px-3 py-1.5 bg-black/70 text-white text-sm font-medium rounded-full">
            {currentSlideIndex + 1}/{totalSlides}
          </div>

          {/* Checklist indicator on last slide */}
          {currentSlide?.type === 'checklist' && (
            <div className="absolute top-3 left-3 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-full">
              💄 Products Used
            </div>
          )}

          {/* Navigation Arrows */}
          {totalSlides > 1 && (
            <>
              {currentSlideIndex > 0 && (
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
                  data-testid="prev-slide-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {currentSlideIndex < totalSlides - 1 && (
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-lg"
                  data-testid="next-slide-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </>
          )}
          
          {/* Dots Indicator */}
          {totalSlides > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlideIndex(index)}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentSlideIndex 
                      ? 'bg-white w-4' 
                      : index === mediaUrls.length 
                        ? 'bg-primary/60 w-1.5' // Checklist dot
                        : 'bg-white/60 w-1.5'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <button 
            data-testid={`like-btn-${post.post_id}`}
            onClick={() => onLike(post.post_id)}
            className="hover:scale-110 transition-transform"
          >
            <Heart 
              className={`w-6 h-6 ${
                isLiked ? 'fill-primary text-primary' : 'text-foreground'
              }`}
            />
          </button>
          <button 
            data-testid={`comment-btn-${post.post_id}`}
            onClick={() => setShowComments(!showComments)}
            className="hover:scale-110 transition-transform"
          >
            <MessageCircle className="w-6 h-6 text-foreground" />
          </button>
          
          {/* Share Button with Dropdown */}
          <div className="relative" ref={shareMenuRef}>
            <button 
              data-testid={`share-btn-${post.post_id}`}
              onClick={() => setShowBottomShareMenu(!showBottomShareMenu)}
              className="hover:scale-110 transition-transform"
            >
              <Share2 className="w-6 h-6 text-foreground" />
            </button>
            
            {showBottomShareMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white rounded-xl shadow-xl border border-border p-3 z-50 min-w-[200px]">
                <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Share to</p>
                
                <button 
                  onClick={() => handleShare('whatsapp')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">📱</span>
                  <span className="font-medium">WhatsApp</span>
                </button>
                
                <button 
                  onClick={() => handleShare('instagram')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-pink-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">📸</span>
                  <span className="font-medium">Instagram</span>
                </button>
                
                <button 
                  onClick={() => handleShare('twitter')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">🐦</span>
                  <span className="font-medium">Twitter</span>
                </button>
                
                <button 
                  onClick={() => handleShare('facebook')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">📘</span>
                  <span className="font-medium">Facebook</span>
                </button>
                
                <button 
                  onClick={() => handleShare('telegram')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">✈️</span>
                  <span className="font-medium">Telegram</span>
                </button>
                
                <div className="border-t border-border my-2"></div>
                
                <button 
                  onClick={() => handleShare('copy')}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors text-left"
                >
                  <span className="text-xl">🔗</span>
                  <span className="font-medium">Copy Link</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Likes */}
        <p className="font-bold text-sm mb-2">{post.likes?.length || 0} likes</p>

        {/* Main Caption (legacy support) */}
        {post.caption && (
          <p className="text-sm mb-2">
            <span className="font-bold mr-2">@{displayName}</span>
            {post.caption}
          </p>
        )}

        {/* Comments */}
        {post.comments && post.comments.length > 0 && (
          <button 
            onClick={() => setShowComments(!showComments)}
            className="text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            View all {post.comments.length} comments
          </button>
        )}

        {showComments && post.comments && (
          <div className="space-y-2 mb-3">
            {post.comments.map(comment => (
              <div key={comment.comment_id} className="text-sm">
                <span className="font-bold mr-2">@{comment.username || comment.user_name?.replace(/\s+/g, '').toLowerCase()}</span>
                {comment.text}
              </div>
            ))}
          </div>
        )}

        {/* Add Comment */}
        {showComments && (
          <div className="flex gap-2 mt-3">
            <input
              data-testid={`comment-input-${post.post_id}`}
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 rounded-xl border border-border bg-white/50 focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none text-sm"
            />
            <button
              data-testid={`comment-submit-${post.post_id}`}
              onClick={handleComment}
              className="px-4 py-2 bg-primary text-white rounded-xl font-medium text-sm hover:scale-105 transition-transform"
            >
              Post
            </button>
          </div>
        )}

        {/* Time */}
        <p className="text-xs text-muted-foreground uppercase mt-2">{timeAgo}</p>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Post</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Checklist Selector */}
            {userChecklists.length > 0 && (
              <div className="mb-4">
                <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Linked Checklist</label>
                <select
                  data-testid="edit-checklist-select"
                  value={editChecklistId}
                  onChange={(e) => setEditChecklistId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-gray-50 focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none text-sm"
                >
                  {userChecklists.map(c => (
                    <option key={c.checklist_id} value={c.checklist_id}>
                      {c.name || 'My Checklist'} ({c.products?.length || 0} products){c.is_pinned ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="space-y-4">
              {mediaUrls.map((url, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <img src={url} alt={`Image ${index + 1}`} className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-muted-foreground mb-1 block">
                      Caption for image {index + 1}
                    </label>
                    <input
                      type="text"
                      value={editCaptions[index] || ''}
                      onChange={(e) => {
                        const newCaptions = [...editCaptions];
                        newCaptions[index] = e.target.value;
                        setEditCaptions(newCaptions);
                      }}
                      className="w-full px-3 py-2 rounded-xl border border-border focus:border-primary outline-none text-sm"
                      placeholder="Add a caption..."
                      maxLength={200}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-3 rounded-xl border border-border hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 btn-primary px-4 py-3"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
